import type { Lang } from '../i18n'

/**
 * The no-value glyph.
 *
 * A cell with nothing behind it renders this and never a zero: an empty cell that read
 * "0.00" would be a figure the data does not support, and a hyphen beside the negative
 * numbers in the same column would read as a minus sign. `scripts/check-dashes.py` allows
 * exactly this one standalone quoted em dash for the same reason.
 */
export const NO_VALUE = '—'

const LOCALE: Record<Lang, string> = { en: 'en-US', ru: 'ru-RU', uk: 'uk-UA' }
// `uk-UA` names a foreign currency by its code ("1 234,00 USD") where `ru-RU` uses the
// symbol; the narrow symbol gives the Ukrainian reader the same "1 234,00 $" in the same width.
const CURRENCY_DISPLAY: Record<Lang, 'symbol' | 'narrowSymbol'> = { en: 'symbol', ru: 'symbol', uk: 'narrowSymbol' }

/** Intl formatters are expensive to build and are asked for once per cell. */
const cache = new Map<string, Intl.NumberFormat>()

function formatter(key: string, build: () => Intl.NumberFormat): Intl.NumberFormat {
  const existing = cache.get(key)
  if (existing) return existing
  const built = build()
  cache.set(key, built)
  return built
}

function numFormatter(lang: Lang, decimals: number): Intl.NumberFormat {
  return formatter(`n:${lang}:${decimals}`, () =>
    new Intl.NumberFormat(LOCALE[lang], {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    }),
  )
}

export function num(value: number | null, lang: Lang, decimals = 0): string {
  if (value === null || !Number.isFinite(value)) return NO_VALUE
  return numFormatter(lang, decimals).format(value)
}

/**
 * Money in the account's currency, in the reader's locale.
 *
 * `ru-RU` renders dollars as "1 234 $" rather than "$1,234", which is what a Russian
 * reader expects of a foreign currency and what the locale is for. The currency comes from
 * the response, never from a constant, because one request covers one currency and the
 * screen must not label a figure with a symbol the account does not report in.
 */
export function money(
  value: number | null,
  currency: string | null,
  lang: Lang,
  decimals = 0,
): string {
  if (value === null || !Number.isFinite(value)) return NO_VALUE
  if (currency === null) return num(value, lang, decimals)
  return moneyFormatter(currency, lang, decimals).format(value)
}

function moneyFormatter(currency: string, lang: Lang, decimals: number): Intl.NumberFormat {
  return formatter(`m:${lang}:${currency}:${decimals}`, () =>
    new Intl.NumberFormat(LOCALE[lang], {
      style: 'currency',
      currency,
      currencyDisplay: CURRENCY_DISPLAY[lang],
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    }),
  )
}

/**
 * `money()` as segments that each end after a digit-group separator, for a renderer that
 * lets a figure break there and nowhere else (GRO-89). Joined, they equal `money()`.
 *
 * No segment boundary falls between the digits and the sign, so a narrow column can put
 * `1 234` over `567,00 $` but never `567,00` over `$`.
 */
export function moneyParts(
  value: number | null,
  currency: string | null,
  lang: Lang,
  decimals = 0,
): string[] {
  if (value === null || !Number.isFinite(value)) return [NO_VALUE]
  const format = currency === null ? numFormatter(lang, decimals) : moneyFormatter(currency, lang, decimals)
  const segments = ['']
  for (const part of format.formatToParts(value)) {
    segments[segments.length - 1] += part.value
    if (part.type === 'group') segments.push('')
  }
  return segments
}

/** A percentage already expressed as 0 to 100, as the contract's rates and benchmarks are. */
export function percent(value: number | null, lang: Lang, decimals = 2): string {
  if (value === null || !Number.isFinite(value)) return NO_VALUE
  return `${num(value, lang, decimals)}%`
}

/** A ratio with two decimals: ROAS, adjusted ROAS, the multiple a delivery step ran at. */
export function ratio(value: number | null, lang: Lang, decimals = 2): string {
  return num(value, lang, decimals)
}

export interface PluralForms {
  one: string
  few: string
  many: string
}

/**
 * The right form of a counted noun.
 *
 * Russian and Ukrainian share the three-form rule, and "1 конверсия / 2 конверсии / 5
 * конверсий" is the difference between an interface written in the language and one
 * translated into it. English uses `one` and `few`; `many` is never reached there.
 */
export function plural(count: number, lang: Lang, forms: PluralForms): string {
  if (lang === 'en') return count === 1 ? forms.one : forms.few
  const mod10 = Math.abs(count) % 10
  const mod100 = Math.abs(count) % 100
  if (mod10 === 1 && mod100 !== 11) return forms.one
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return forms.few
  return forms.many
}

/** `{name:one|other}` or `{name:one|few|many}`: the counted word, inline in the string. */
const COUNTED = /\{(\w+):([^{}]*)\}/g

/**
 * One form of a counted word, chosen by the count in `vars[key]`.
 *
 * The number of forms names the rule: two are English (`one`, `other`), three are Russian
 * (`one`, `few`, `many`), which is also the Ukrainian rule. `i18n.test.ts` holds every
 * dictionary string to the count its language takes. A count that is missing or not a
 * number takes the last form, the one a no-value glyph reads with.
 */
function counted(template: string, vars: Record<string, string | number | null>): string {
  return template.replace(COUNTED, (whole, key: string, body: string) => {
    const forms = body.split('|')
    if (!(key in vars) || (forms.length !== 2 && forms.length !== 3)) return whole
    const value = vars[key]
    const count = value === null || value === '' ? Number.NaN : Number(value)
    if (!Number.isFinite(count)) return forms[forms.length - 1]
    return forms.length === 2
      ? plural(count, 'en', { one: forms[0], few: forms[1], many: forms[1] })
      : plural(count, 'ru', { one: forms[0], few: forms[1], many: forms[2] })
  })
}

/**
 * `%name` substitution, the dictionary's own placeholder syntax, after the counted words.
 *
 * Counted words resolve first, so a substituted value that carries braces, a buyer's ad
 * name for one, is never read as a form list. Placeholders resolve in one pass over the
 * template, longer names first, so a substituted value that carries `%name`, a company
 * renamed to "50 %to go" for one, stays as written.
 */
export function fill(template: string, vars: Record<string, string | number | null>): string {
  const keys = Object.keys(vars).sort((left, right) => right.length - left.length)
  const withCounts = counted(template, vars)
  if (keys.length === 0) return withCounts
  const names = new RegExp(`%(${keys.map((key) => key.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|')})`, 'g')
  return withCounts.replace(names, (_, key: string) => (vars[key] === null ? NO_VALUE : String(vars[key])))
}
